/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import "./App.scss";

import { BrowserAuthorizationClient } from "@itwin/browser-authorization";
import { IModelApp, ScreenViewport } from "@itwin/core-frontend";
import { initializeFrontendTiles } from "@itwin/frontend-tiles";
import { FillCentered } from "@itwin/core-react";
import { ProgressLinear } from "@itwin/itwinui-react";
import {
  MeasureTools
} from "@itwin/measure-tools-react";
import {
  PropertyGridManager, PropertyGridUiItemsProvider
} from "@itwin/property-grid-react";
import {
  TreeWidget, TreeWidgetUiItemsProvider
} from "@itwin/tree-widget-react";
import {
  useAccessToken,
  Viewer, ViewerAuthorization, ViewerNavigationToolsProvider,
  ViewerPerformance,
  ViewerStatusbarItemsProvider
} from "@itwin/web-viewer-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { history } from "./history";
import { BeDuration } from "@itwin/core-bentley";
import { Point3d } from "@itwin/core-geometry";
import { Frustum } from "@itwin/core-common";

const App: React.FC = () => {
  const [iModelId, setIModelId] = useState<string | null>(null);
  const [iTwinId, setITwinId] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [signingIn, setSigninIn] = useState<boolean>(true);

  const accessToken = useAccessToken();

  const authClient = useMemo(
    () =>
      new BrowserAuthorizationClient({
        scope: process.env.IMJS_AUTH_CLIENT_SCOPES ?? "",
        clientId: (globalThis.IMJS_URL_PREFIX) ? process.env.IMJS_QA_AUTH_CLIENT_CLIENT_ID! : process.env.IMJS_AUTH_CLIENT_CLIENT_ID!,
        redirectUri: new URL("/signin-callback", window.location.origin).href,
        postSignoutRedirectUri: process.env.IMJS_AUTH_CLIENT_LOGOUT_URI,
        responseType: "code",
        authority: (globalThis.IMJS_URL_PREFIX) ? "https://qa-ims.bentley.com" : "https://ims.bentley.com",
      }),
    []
  );
  ViewerAuthorization.client = authClient;

  const login = useCallback(async () => {
    try {
      await authClient.signInSilent();
    } catch {
      await authClient.signIn();
    }
  }, [authClient]);

  useEffect(() => {
    void login();
  }, [login]);

  useEffect(() => {
    if (accessToken) {
      setSigninIn(false);
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has("iModelId")) {
        if (urlParams.has("iTwinId")) {
          setExportError("Please, add a valid iModelId query parameter in the url and refresh the page.");  
        }
        else {
          // iModelId&iTwinId have not been defined, let's change the URL to display the default iModelId/iTwinId

          let queryString = `?iTwinId=${process.env.IMJS_ITWIN_ID}&iModelId=${process.env.IMJS_IMODEL_ID}`;
          if (urlParams.has("httpConcurrency")) {
            queryString += `&httpConcurrency=${urlParams.get("httpConcurrency")}`;
          }
          if (urlParams.has("useOld")) {
            queryString += `&useOld=1`;
          }

          if (window.location.search.length) {
            const newLocation = window.location.href.replace(window.location.search, queryString);
            window.location.replace(newLocation);
          } else {
            window.location.replace(window.location.href + queryString);
          }
        }
      }
      else if (!urlParams.has("iTwinId")) {
        setExportError("Please, add a valid iTwinId query parameter in the url and refresh the page.");
      }
      else {
        setITwinId(urlParams.get("iTwinId") as string);
        setIModelId(urlParams.get("iModelId") as string);

        if (!urlParams.has("env")) {
          const env = globalThis.IMJS_URL_PREFIX;
          const envParam = env === "" ? "prod" : env.replace("-", "");
          history.push(window.location.search + "&env=" + envParam);
        }
      }
    }
  }, [accessToken]);

  useEffect(() => {
    const fetchMeshUrl = async () => {
      // Lookup tiles in Mesh Export Service
      const headers = {
        Authorization: accessToken!,
        Accept: "application/vnd.bentley.itwin-platform.v1+json",
        Prefer: "return=representation"
      };
      let validExports = [];
      try {
        
        var getExportUrl = `https://${globalThis.IMJS_URL_PREFIX}api.bentley.com/mesh-export/?iModelId=${iModelId}&$orderBy=date:desc`;
        const result = await (await fetch(getExportUrl, { headers })).json();
        if (!result.exports || !result.exports.length) {
          setExportError("No exports found for this iModel.");
          return console.error("Failed loading exports for this iModel");
        }
        validExports = result.exports.filter((x: any) => x.request.exportType === "IMODEL" && x.status === "Complete");
      } catch (error) {
        setExportError("There was an error getting exports for this iModel: " + error);
        return console.error("Failed loading exports for this iModel");
      }
      
      if (!validExports.length) {
        setExportError("No valid (IMODEL) mesh export found for this iModel.");
        return console.error("No exports available for this iModel");
      }
      const connectionProps = validExports[0]; // It is picking the latest one
      let baseUrl = new URL(connectionProps._links.mesh.href);
      baseUrl.pathname = baseUrl.pathname + "/tileset.json";

      // const override = localStorage.getItem("CDN_OVERRIDE");
      // if (override) {
      //   (globalThis as any).httpConcurrency = localStorage.getItem("CDN_CONCURRENCY") || 20;
      //   const newBase = new URL(baseUrl.pathname, override);
      //   newBase.search = baseUrl.search + (new URL(override)).search.replace("?", "&");
      //   baseUrl = newBase;
      // }
      setBaseUrl(baseUrl.href);

      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has("useOld")) {
        initializeFrontendTiles({
          computeSpatialTilesetBaseUrl: async () => new URL(baseUrl!),
        });
      }
    }
    if (accessToken && iModelId) {
      fetchMeshUrl().catch(console.error);
    }
  }, [accessToken, iModelId]);


  /** NOTE: This function will execute the "Fit View" tool after the iModel is loaded into the Viewer.
   * This will provide an "optimal" view of the model. However, it will override any default views that are
   * stored in the iModel. Delete this function and the prop that it is passed to if you prefer
   * to honor default views when they are present instead (the Viewer will still apply a similar function to iModels that do not have a default view).
   */
  const viewConfiguration = useCallback(async (viewPort: ScreenViewport) => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("httpConcurrency"))
      IModelApp.tileAdmin.channels.iModelChannels.cloudStorage.concurrency = parseInt(urlParams.get("httpConcurrency")||"6",10);

    viewPort.turnCameraOn();
    await BeDuration.wait(0);
    viewPort.viewFlags = viewPort.viewFlags.copy({ backgroundMap: false });

    if (iModelId === "8176e2ba-cefd-4a35-87a8-ab80e5380d07") {
      const f = new Frustum();
      f.setFromCorners([
        Point3d.fromJSON({ x: -9205.76319337469, y: 28271.68906137141, z: -26681.277577986337 }),
        Point3d.fromJSON({ x: 28277.52520793175, y: -9211.599339935081, z: -26681.277577986322 }),
        Point3d.fromJSON({ x: 2223.382880421437, y: 39700.83513516754, z: -3822.985430394074 }),
        Point3d.fromJSON({ x: 39706.671281727875, y: 2217.5467338610433, z: -3822.985430394059 }),
        Point3d.fromJSON({ x: -52.086861741314195, y: -57.708047839764255, z: 50.20360510513082 }),
        Point3d.fromJSON({ x: -51.87190127983086, y: -57.92300830124759, z: 50.20360510513082 }),
        Point3d.fromJSON({ x: -52.02131747827942, y: -57.642503576729474, z: 50.33469363120037 }),
        Point3d.fromJSON({ x: -51.80635701679607, y: -57.85746403821282, z: 50.33469363120037 }),
      ]);
      viewPort.setupViewFromFrustum(f);
    }

    await viewPort.waitForSceneCompletion();
    ViewerPerformance.addMark("TilesLoaded");
    ViewerPerformance.addMeasure(
      "TileTreesLoaded",
      "ViewStateCreation",
      "TilesLoaded"
    );
  }, [iModelId]);

  const viewCreatorOptions = useMemo(
    () => ({
      viewportConfigurer: viewConfiguration,
      skyboxOn: true,
      cameraOn: true,
    }),
    [viewConfiguration]
  );

  const onIModelAppInit = useCallback(async () => {
    await TreeWidget.initialize();
    await PropertyGridManager.initialize();
    await MeasureTools.startup();
  }, []);

  return (
    <div className="viewer-container">
      {(signingIn) && (
        <FillCentered>
          <div className="signin-content">
            <ProgressLinear indeterminate={true} labels={["Signing in..."]} />
          </div>
        </FillCentered>
      )}
      {(!signingIn && !exportError && iModelId && !baseUrl) && (
        <FillCentered>
          <div className="signin-content">
            <ProgressLinear indeterminate={true} labels={["Loading mesh export..."]} />
          </div>
        </FillCentered>
      )}
      {(exportError) && (
        <FillCentered>
          { exportError }
        </FillCentered>
      )}
      {baseUrl && (<Viewer
        iTwinId={iTwinId ?? ""}
        iModelId={iModelId ?? ""}
        authClient={authClient}
        viewCreatorOptions={viewCreatorOptions}
        enablePerformanceMonitors={true} // see description in the README (https://www.npmjs.com/package/@itwin/web-viewer-react)
        onIModelAppInit={onIModelAppInit}
        uiProviders={[
          new ViewerNavigationToolsProvider(),
          new ViewerStatusbarItemsProvider({
            accuSnapModePicker: true,
            messageCenter: true,
            postToolAssistanceSeparator: true,
            preToolAssistanceSeparator: true,
            selectionInfo: true,
            selectionScope: true,
            tileLoadIndicator: true
          }),
          new TreeWidgetUiItemsProvider(),
          new PropertyGridUiItemsProvider({
            enableCopyingPropertyText: true,
          }),
        ]}
      />)}
    </div>
  );
};

export default App;
